import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import dart from 'react-syntax-highlighter/dist/esm/languages/prism/dart';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import kotlin from 'react-syntax-highlighter/dist/esm/languages/prism/kotlin';
import objectivec from 'react-syntax-highlighter/dist/esm/languages/prism/objectivec';
import swift from 'react-syntax-highlighter/dist/esm/languages/prism/swift';

import DEFAULT_EASE from 'constants/default-ease';

SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('dart', dart);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('kotlin', kotlin);
SyntaxHighlighter.registerLanguage('objectivec', objectivec);
SyntaxHighlighter.registerLanguage('swift', swift);

const wrapperVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.5,
      staggerChildren: 0.05,
    },
  },
};

const tabsWrapperVariants = {
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const tabVariants = {
  initial: {
    translateY: '100%',
  },
  animate: {
    translateY: 0,
    transition: {
      duration: 1,
      ease: DEFAULT_EASE,
    },
  },
};

const codeVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 1,
      ease: 'linear',
    },
  },
};

const classNamesCode = 'scrollbar-hidden overflow-x-auto px-5 xl:px-4';

const CodeTabs = ({ className, items, withAnimation, animate }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        className={clsx('card-shadow overflow-hidden rounded-lg bg-black', className)}
        initial={withAnimation && 'initial'}
        animate={withAnimation && animate && 'animate'}
        variants={wrapperVariants}
        aria-hidden
      >
        <m.ul
          className="scrollbar-hidden flex space-x-0.5 overflow-x-auto overflow-y-hidden"
          variants={tabsWrapperVariants}
        >
          {items.map(({ name }, index) => (
            <m.li className="grow" key={index} variants={tabVariants}>
              <button
                className={clsx(
                  'block w-full cursor-pointer whitespace-nowrap bg-gray-10 py-2.5 px-4 text-xs font-medium leading-none text-gray-6 transition-colors duration-200 hover:bg-gray-9 hover:text-gray-6 xl:px-2.5',
                  activeIndex === index && '!bg-black !text-white',
                  items.length === 1 && '!bg-gray-9 !text-white'
                )}
                type="button"
                onClick={() => setActiveIndex(index)}
              >
                {name}
              </button>
            </m.li>
          ))}
        </m.ul>
        <m.div
          className="scrollbar-hidden h-[calc(100%-32px)] overflow-y-auto py-7 text-[14px] text-white xl:text-[13px]"
          variants={codeVariants}
        >
          <SyntaxHighlighter
            className={clsx(classNamesCode, 'sm:hidden')}
            language={items[activeIndex].language}
            useInlineStyles={false}
            showLineNumbers
          >
            {items[activeIndex].code}
          </SyntaxHighlighter>
          <SyntaxHighlighter
            className={clsx(classNamesCode, 'hidden sm:block')}
            language={items[activeIndex].language}
            useInlineStyles={false}
          >
            {items[activeIndex].code}
          </SyntaxHighlighter>
        </m.div>
      </m.div>
    </LazyMotion>
  );
};

CodeTabs.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      language: PropTypes.oneOf([
        'csharp',
        'dart',
        'java',
        'javascript',
        'kotlin',
        'objectivec',
        'swift',
      ]).isRequired,
    })
  ).isRequired,
  withAnimation: PropTypes.bool,
  animate: PropTypes.bool,
};

CodeTabs.defaultProps = {
  className: null,
  withAnimation: false,
  animate: false,
};

export default CodeTabs;
